import { makeAutoObservable, runInAction } from 'mobx';
import { MessageApi } from './MessageApi';
import {
  ChatMessageDTO,
  ChatRoomDTO,
  ChatRoomUpdateDTO,
  ChatRoomView,
  ChatUserBrief,
} from './MessageType';
import {
  fetchChatUserInfo,
  getMemberRole,
  getPeerUserId,
  getSelfUserId,
} from './MessageConfig';
import {
  computeUnreadCount,
  getHistoryCutoffTime,
  isWithinHistoryWindow,
  messageBrief,
  roomListSortKey,
  toDate,
} from './MessageUtil';

class MessageModel {
  rooms: ChatRoomView[] = [];
  messagesByRoom = new Map<number, ChatMessageDTO[]>();
  selectedRoomId: number | null = null;
  loading = false;
  syncing = false;
  hasMoreHistory = false;
  lastSyncTime = getHistoryCutoffTime();
  readSeqMap = new Map<number, number>();
  userInfoCache = new Map<string, ChatUserBrief>();
  pollTimer: ReturnType<typeof setInterval> | null = null;
  inputText = '';
  emojiPanelVisible = false;
  sending = false;

  constructor() {
    makeAutoObservable(this);
  }

  get cutoffTime(): Date {
    return getHistoryCutoffTime();
  }

  get sortedRooms(): ChatRoomView[] {
    return [...this.rooms].sort((a, b) => roomListSortKey(b) - roomListSortKey(a));
  }

  get selectedRoom(): ChatRoomView | undefined {
    if (this.selectedRoomId == null) {
      return undefined;
    }
    return this.rooms.find((r) => r.id === this.selectedRoomId);
  }

  getVisibleMessages(roomId: number): ChatMessageDTO[] {
    const list = this.messagesByRoom.get(roomId) || [];
    const cutoff = this.cutoffTime;
    return list
      .filter((m) => isWithinHistoryWindow(m.time, cutoff))
      .sort((a, b) => a.seqInRoom - b.seqInRoom);
  }

  reset() {
    this.stopPolling();
    this.rooms = [];
    this.messagesByRoom.clear();
    this.selectedRoomId = null;
    this.hasMoreHistory = false;
    this.lastSyncTime = getHistoryCutoffTime();
    this.readSeqMap.clear();
    this.inputText = '';
    this.emojiPanelVisible = false;
  }

  startPolling() {
    if (this.pollTimer) {
      return;
    }
    this.pollTimer = setInterval(() => {
      void this.sync();
    }, 5000);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async init() {
    this.loading = true;
    this.userInfoCache.clear();
    this.lastSyncTime = getHistoryCutoffTime();
    await this.sync();
    runInAction(() => {
      this.loading = false;
    });
    if (this.selectedRoomId == null && this.sortedRooms.length > 0) {
      this.selectRoom(this.sortedRooms[0].id);
    }
    this.startPolling();
  }

  async sync() {
    if (this.syncing) {
      return;
    }
    this.syncing = true;
    try {
      const readSeqs = Array.from(this.readSeqMap.entries()).map(
        ([roomId, readSeq]) => ({ roomId, readSeq }),
      );
      const result = await MessageApi.syncAllChatRooms(
        this.lastSyncTime,
        readSeqs,
      );
      if (!result) {
        return;
      }
      runInAction(() => {
        if (result.hasMore) {
          this.hasMoreHistory = true;
        }
        if (result.updates?.length) {
          for (const update of result.updates) {
            this.applyUpdate(update);
          }
        }
        if (result.lastSyncTime) {
          this.lastSyncTime = toDate(result.lastSyncTime);
        }
        for (const roomId of readSeqs.map((r) => r.roomId)) {
          this.readSeqMap.delete(roomId);
        }
      });
      await this.prefetchAllPeerUsers();
    } finally {
      runInAction(() => {
        this.syncing = false;
      });
    }
  }

  async prefetchAllPeerUsers() {
    for (const room of this.rooms) {
      await this.ensurePeerUserInfo(room.id);
    }
  }

  async ensurePeerUserInfo(roomId: number) {
    const room = this.rooms.find((r) => r.id === roomId);
    if (!room || room.isGroup) {
      return;
    }
    const peerId = getPeerUserId(room, this.getVisibleMessages(roomId));
    if (peerId) {
      await this.getUserInfo(peerId, roomId);
    }
  }

  private applyUpdate(update: ChatRoomUpdateDTO) {
    const selfId = getSelfUserId() || '';
    const dto = update.chatRoom;
    const incoming = update.messages || [];
    const existing = this.messagesByRoom.get(dto.id) || [];
    const mergedMap = new Map<number, ChatMessageDTO>();
    for (const m of existing) {
      mergedMap.set(m.id, m);
    }
    for (const m of incoming) {
      mergedMap.set(m.id, m);
    }
    const merged = Array.from(mergedMap.values());
    this.messagesByRoom.set(dto.id, merged);

    const visible = merged.filter((m) =>
      isWithinHistoryWindow(m.time, this.cutoffTime),
    );
    const lastMsg = visible.sort((a, b) => b.seqInRoom - a.seqInRoom)[0];
    const unread = computeUnreadCount(dto, visible, selfId);

    const roomView: ChatRoomView = {
      ...dto,
      lastMessage: lastMsg ? messageBrief(lastMsg) : '',
      lastMessageTime: lastMsg ? toDate(lastMsg.time) : toDate(dto.lastActiveTime),
      unreadCount: unread,
    };

    const idx = this.rooms.findIndex((r) => r.id === dto.id);
    if (idx >= 0) {
      this.rooms[idx] = roomView;
    } else {
      this.rooms.push(roomView);
    }
  }

  selectRoom(roomId: number) {
    this.selectedRoomId = roomId;
    this.emojiPanelVisible = false;
    const msgs = this.getVisibleMessages(roomId);
    const maxSeq = msgs.reduce((max, m) => Math.max(max, m.seqInRoom), 0);
    if (maxSeq > 0) {
      this.markRoomRead(roomId, maxSeq);
    }
    void this.ensurePeerUserInfo(roomId);
  }

  markRoomRead(roomId: number, readSeq: number) {
    const selfId = getSelfUserId();
    if (!selfId) {
      return;
    }
    const prev = this.readSeqMap.get(roomId) ?? 0;
    const next = Math.max(prev, readSeq);
    this.readSeqMap.set(roomId, next);

    const idx = this.rooms.findIndex((r) => r.id === roomId);
    if (idx >= 0) {
      const room = this.rooms[idx];
      const readSeqMap = { ...(room.readSeqMap || {}), [selfId]: next };
      this.rooms[idx] = {
        ...room,
        unreadCount: 0,
        readSeqMap,
      };
    }
  }

  async getUserInfo(userId: string, roomId?: number): Promise<ChatUserBrief> {
    const cacheKey = roomId != null ? `${roomId}:${userId}` : userId;
    const cached = this.userInfoCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    const room = roomId != null ? this.rooms.find((r) => r.id === roomId) : undefined;
    const memberRole = getMemberRole(room, userId);
    const info = await fetchChatUserInfo(userId, memberRole);
    runInAction(() => {
      this.userInfoCache.set(cacheKey, info);
      this.userInfoCache.set(userId, info);
    });
    return info;
  }

  async sendText(roomId: number) {
    const text = this.inputText.trim();
    const selfId = getSelfUserId();
    if (!text || !selfId || this.sending) {
      return;
    }
    this.sending = true;
    this.inputText = '';
    this.emojiPanelVisible = false;
    try {
      await MessageApi.appendChatMessage(roomId, text, 'text');
      await this.sync();
    } finally {
      runInAction(() => {
        this.sending = false;
      });
    }
  }

  async sendImageCard(roomId: number, contentJson: string) {
    const selfId = getSelfUserId();
    if (!selfId || this.sending) {
      return;
    }
    this.sending = true;
    try {
      await MessageApi.appendChatMessage(roomId, contentJson, 'image');
      await this.sync();
    } finally {
      runInAction(() => {
        this.sending = false;
      });
    }
  }

  toggleEmojiPanel() {
    this.emojiPanelVisible = !this.emojiPanelVisible;
  }

  closeEmojiPanel() {
    this.emojiPanelVisible = false;
  }
}

export const messageModel = new MessageModel();
